import { Button, Descriptions, Divider, message, Modal, Space, Table, Tag, Typography } from "antd"
import {
  collection,
  doc,
  onSnapshot,
  query,
  serverTimestamp,
  where,
  writeBatch,
  arrayUnion,
} from "firebase/firestore"
import React, { useEffect, useState } from "react"
import { db } from "../../../firebase/config"
import { useAuthContext } from "../../../hooks/useAuthContext"
import moment from "moment"
import { EuroCircleOutlined, FileOutlined, PlusCircleOutlined } from "@ant-design/icons"
import "./AddCustomInvoice.css"

const { Link, Text } = Typography

export default function AddCustomInvoice({ record }) {
  const { user } = useAuthContext()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [tableLoading, setTableLoading] = useState(false)
  const [invoices, setInvoices] = useState([])
  const [selectedInvoice, setSelectedInvoice] = useState(null)

  const showModal = () => {
    setIsModalOpen(true)
  }
  const handleOk = async () => {
    if (selectedInvoice) {
      await updateOrderWithInvoice()
    }
    setSelectedInvoice(null)
  }
  const handleCancel = () => {
    setIsModalOpen(false)
    setSelectedInvoice(null)
    setInvoices([])
  }

  const handleSelectInvoice = (value) => {
    setSelectedInvoice(value)
  }

  useEffect(() => {
    if (isModalOpen) {
      setTableLoading(true)

      const invoicesRef = collection(db, "invoices")
      let orderQuery = query(
        invoicesRef,
        where("userId", "==", user.uid)
      )
      const unsubscribe = onSnapshot(orderQuery, (snapshot) => {
        let results = []
        snapshot.docs.forEach((doc) => {
          const data = doc.data()
          // Client-side filtering - show all custom invoices that haven't been used
          if (data.customInvoice === true && data.invoiceUsed === false) {
            results.push({ ...data, id: doc.id })
          }
        })
        setInvoices(results)
        setTableLoading(false)
      })
      return () => unsubscribe()
    }
  }, [user, record, isModalOpen])

  // 3. Replace this entire function with the updated version
  const updateOrderWithInvoice = async () => {
    // Create a batch
    const batch = writeBatch(db)

    try {
      // Reference to the order document
      const orderRef = doc(db, "orders", record.id)
      // Reference to the invoice document
      const invoiceRef = doc(db, "invoices", selectedInvoice.id)

      // --- Update for the Order ---
      batch.update(orderRef, {
        hasInvoice: true,
        invoiceAdded: serverTimestamp(),
        customInvoiceAdded: true,
        invoicePaymentStatus: 1,
        invoiceDownloaded: false,
        invoice: {
          invoiceId: selectedInvoice.id,
          invoiceUrl: selectedInvoice.downloadUrl,
          created: selectedInvoice.created,
          type: selectedInvoice.docType,
          language: selectedInvoice.pdfLanguage,
          name: selectedInvoice.docPrefix + String(selectedInvoice.docNumber).padStart(4, "0"),
        },
      })

      // --- Update for the Invoice ---
      batch.update(invoiceRef, {
        orderReferenceId: arrayUnion(record.id), // Push the order ID into the array
      })

      // Commit the batch
      await batch.commit()

      message.success("Order and Invoice were updated successfully.")
    } catch (error) {
      console.error("Error updating documents:", error)
      message.error("Failed to update order and invoice.")
    } finally {
      setIsModalOpen(false)
    }
  }

  const computedProductFilters = invoices.reduce((acc, invoice) => {
    invoice.prices.forEach((price) => {
      if (!acc.find((option) => option.value === price.product)) {
        acc.push({ text: price.product, value: price.product })
      }
    })
    return acc
  }, [])

  const columns = [
    {
      title: "Series",
      dataIndex: "docPrefix",
      width: "5%",
      align: "left",
    },
    {
      title: "Number",
      dataIndex: "docNumber",
      width: "5%",
      align: "left",
      sorter: (a, b) => a.docNumber - b.docNumber,
      render: (value) => <Text>{String(value).padStart(4, "0")}</Text>,
    },
    {
      title: "Products",
      dataIndex: "prices",
      align: "left",
      render: (prices) => (
        <>
          {prices.map((price, index) => (
            <Space>
              <Tag key={index} color="blue">
                {price.product}
              </Tag>
              <Tag color="#f5222d">Count : {price.count}</Tag>
              <Tag color="#52c41a">
                {" "}
                {price.sum} <EuroCircleOutlined />
              </Tag>
            </Space>
          ))}
        </>
      ),
      filters: computedProductFilters,
      onFilter: (value, record) => record.prices.some((price) => price.product === value),
    },
    {
      title: "Date",
      dataIndex: ["documentDate", "seconds"],
      width: "10%",
      align: "left",
      render: (value) => <Text>{moment.unix(value).format("DD.MM.YYYY")}</Text>,
    },
    {
      title: "Neto (eur)",
      dataIndex: ["totalData", "totalNetoSum"],
      width: "10%",
      align: "right",
      render: (value) => <Text>{value}</Text>,
    },
    {
      title: "Vat (eur)",
      dataIndex: ["totalData", "totalVat"],
      width: "10%",
      align: "right",
      render: (value) => <Text>{value}</Text>,
    },
    {
      title: "Total (eur)",
      dataIndex: ["totalData", "totalSum"],
      width: "10%",
      align: "right",
      render: (value) => <Text>{value}</Text>,
    },
    {
      title: "File",
      dataIndex: "downloadUrl",
      width: "15%",
      align: "center",
      render: (value) => (
        <Link href={value}>
          Invoice File <FileOutlined />
        </Link>
      ),
    },
    {
      title: "Select",
      width: "15%",
      align: "center",
      render: (value) => {
        if (selectedInvoice && value.id === selectedInvoice.id) {
          return (
            <Button size="small" type="primary" disabled onClick={() => handleSelectInvoice(value)}>
              Selected...
            </Button>
          )
        } else {
          return (
            <Button
              size="small"
              type="primary"
              icon={<PlusCircleOutlined />}
              onClick={() => handleSelectInvoice(value)}>
              Select This
            </Button>
          )
        }
      },
    },
  ]

  return (
    <>
      {record.hasProforma ? (
        <Text disabled>Can't add custom</Text>
      ) : (
        <Link onClick={showModal}>Add Custom Invoice</Link>
      )}
      <Modal open={isModalOpen} onOk={handleOk} okText="Add" width={1600} onCancel={handleCancel}>
        {selectedInvoice != null ? (
          <Descriptions title="Selected Invoice">
            <Descriptions.Item label="Invoice Number">
              {selectedInvoice.docPrefix + String(selectedInvoice.docNumber).padStart(4, "0")}
            </Descriptions.Item>
            <Descriptions.Item label="Date">
              {moment.unix(selectedInvoice.documentDate.seconds).format("DD.MM.YYYY")}
            </Descriptions.Item>
            <Descriptions.Item label="Client">{selectedInvoice.client.client}</Descriptions.Item>
            <Descriptions.Item label="Products on invoice">
              {selectedInvoice.prices.map((price, index) => (
                <Tag color="blue" key={index}>
                  {price.product} | {price.count} <EuroCircleOutlined />
                </Tag>
              ))}
            </Descriptions.Item>
          </Descriptions>
        ) : (
          <Text type="secondary">Please select invoice first...</Text>
        )}
        <Divider />
        <Table
          loading={tableLoading}
          dataSource={invoices}
          columns={columns}
          rowKey={(record) => record.id}
          rowClassName={(record) =>
            selectedInvoice && record.id === selectedInvoice.id ? "selected-row" : ""
          }
        />
      </Modal>
    </>
  )
}
